import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MuiMaterials } from "../../@index.js";
import * as global from "../../data/global";
import { getWindowChromeThemeColor } from "./windowChromeTheme.js";
import {
    confirmDialogCardSx,
    confirmDialogTitleSx,
    confirmDialogButtonSx,
    confirmDialogActionsSx,
} from "./ConfirmDialog.js";
import { comparisonFieldSx } from "./actionButtons.js";
import { resolveSiteDisplayName } from "./siteWindowNav.js";
import { ensureSiteInfosLoaded } from "./siteInfosCatalog.js";
import { withScrollHintMenuProps } from "./ScrollHintPaper.js";

/**
 * Sentinel value returned by <SiteSelectPhase> when the user picks the "Create New Site" row.
 * Callers that don't support creation can pass `allowCreateNewSite={false}` to hide it.
 */
export const NEW_SITE_OPTION = "__new_site__";

/**
 * Sentinel for "All Sites" (e.g. Site Versioning bulk view). Not a real site GUID.
 */
export const ALL_SITES_OPTION = "__all_sites__";

/**
 * Site Setup's centered "Select site" card, extracted so any main-menu window can front-load
 * its picker instead of expecting the site to be preselected from /sites. Deliberately does
 * not touch `global.gw.selectedSite`; parent decides what "confirm" means for its workflow.
 *
 * Renders inside an existing <WindowChrome> body — it fills the space, not the whole screen —
 * so the parent still owns the header, close button, and light/dark theme.
 *
 * The `useSitePickerFlow` hook below packages the common two-phase state machine (pick then
 * load) that Site Setup uses, and is the recommended way to integrate this card with a body
 * that needs a site GUID to load its data.
 */
export default function SiteSelectPhase({
    title = "Select site",
    description = "",
    value = "",
    onChange,
    onCancel,
    onContinue,
    loading = false,
    filterSite = null,
    allowCreateNewSite = false,
    createNewSiteLabel = "＋ Create New Site",
    includeAllSites = false,
    allSitesLabel = "All Sites",
    continueLabel = "Continue",
    cancelLabel = "Cancel",
    disableCancel = false,
}) {
    const navigate = useNavigate();
    const textSecondary = getWindowChromeThemeColor("#666666", "#c4a574");
    const pageBg = getWindowChromeThemeColor("#e8e8e8", "#1a1a1a");
    const [pickerValue, setPickerValue] = useState(value || "");
    const [siteInfosLen, setSiteInfosLen] = useState(() => (global.gw?.siteInfos ?? []).length);

    useEffect(() => {
        setPickerValue(value || "");
    }, [value]);

    // After refresh, siteInfos may populate after the select already has a GUID value.
    useEffect(() => {
        let cancelled = false;
        ensureSiteInfosLoaded().then(() => {
            if (cancelled) return;
            const n = (global.gw?.siteInfos ?? []).length;
            setSiteInfosLen((prev) => (prev === n ? prev : n));
        });
        const sync = () => {
            const n = (global.gw?.siteInfos ?? []).length;
            setSiteInfosLen((prev) => (prev === n ? prev : n));
        };
        sync();
        const id = setInterval(sync, 250);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    const siteOptions = useMemo(() => {
        return (global.gw?.siteInfos ?? [])
            .filter((s) => {
                if (!s?.siteGuid) return false;
                if (filterSite && !filterSite(s)) return false;
                return true;
            })
            .map((s) => ({
                siteGuid: s.siteGuid,
                siteName: s.siteName || resolveSiteDisplayName(s.siteGuid),
            }))
            .sort((a, b) =>
                String(a.siteName).localeCompare(String(b.siteName), undefined, { sensitivity: "base" }),
            );
    }, [filterSite, loading, siteInfosLen]);

    // Prefer the catalog's GUID casing/format so MUI Select matches a MenuItem.
    useEffect(() => {
        if (!pickerValue || siteOptions.length === 0) return;
        const match = siteOptions.find(
            (s) =>
                global.normalizeGuidForCompare(s.siteGuid) ===
                global.normalizeGuidForCompare(pickerValue),
        );
        if (match && match.siteGuid !== pickerValue) {
            setPickerValue(match.siteGuid);
        }
    }, [siteOptions, pickerValue]);

    const handleChange = useCallback(
        (e) => {
            const next = e.target.value;
            setPickerValue(next);
            onChange?.(next);
        },
        [onChange],
    );

    const handleCancel = useCallback(() => {
        if (onCancel) onCancel();
        else navigate("/sites", { replace: true });
    }, [onCancel, navigate]);

    const displayNameForGuid = useCallback(
        (guid) => resolveSiteDisplayName(guid, siteOptions),
        [siteOptions],
    );

    // A site is already chosen and the window is loading it. The window's own
    // overlay is the spinner; rendering another one here stacks a second.
    if (loading && (pickerValue || value)) {
        return null;
    }

    return (
        <MuiMaterials.Box
            sx={{
                flex: 1,
                alignSelf: "stretch",
                width: "100%",
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
                boxSizing: "border-box",
                backgroundColor: pageBg,
            }}
        >
                <MuiMaterials.Paper
                    elevation={0}
                    sx={{
                        ...confirmDialogCardSx(),
                        // Kill any theme/Paper residual outline that reads as a black border.
                        border: "none !important",
                        boxShadow: "none !important",
                        outline: "none",
                        backgroundImage: "none",
                    }}
                >
                <MuiMaterials.Typography variant="h6" component="h2" sx={{ ...confirmDialogTitleSx(), mb: 2, fontWeight: 600 }}>
                    {title}
                </MuiMaterials.Typography>
                {description ? (
                    <MuiMaterials.Typography variant="body2" sx={{ mb: 2, color: textSecondary }}>
                        {description}
                    </MuiMaterials.Typography>
                ) : null}
                <MuiMaterials.TextField
                    select
                    fullWidth
                    size="small"
                    label="Site"
                    value={pickerValue}
                    disabled={loading}
                    onChange={handleChange}
                    sx={{ mb: 1, ...comparisonFieldSx() }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        endAdornment: loading ? (
                            <MuiMaterials.Box sx={{ display: "flex", pr: 1 }}>
                                <MuiMaterials.RotatingLines
                                    strokeColor={getWindowChromeThemeColor("#333333", "#f4cc8e")}
                                    strokeWidth="3"
                                    animationDuration="0.72"
                                    width="22"
                                    visible
                                />
                            </MuiMaterials.Box>
                        ) : null,
                    }}
                    SelectProps={{
                        displayEmpty: true,
                        MenuProps: withScrollHintMenuProps(),
                        renderValue: (guid) => {
                            if (!guid) {
                                return (
                                    <MuiMaterials.Typography
                                        component="span"
                                        sx={{ color: textSecondary, fontStyle: "italic" }}
                                    >
                                        {loading ? "Loading sites…" : "Select a site"}
                                    </MuiMaterials.Typography>
                                );
                            }
                            if (guid === NEW_SITE_OPTION) return createNewSiteLabel;
                            if (guid === ALL_SITES_OPTION) return allSitesLabel;
                            return displayNameForGuid(guid);
                        },
                    }}
                >
                    <MuiMaterials.MenuItem value="">
                        <em>{loading ? "Loading sites…" : "Select a site"}</em>
                    </MuiMaterials.MenuItem>
                    {includeAllSites ? (
                        <MuiMaterials.MenuItem value={ALL_SITES_OPTION} sx={{ fontWeight: 600 }}>
                            {allSitesLabel}
                        </MuiMaterials.MenuItem>
                    ) : null}
                    {allowCreateNewSite ? (
                        <MuiMaterials.MenuItem value={NEW_SITE_OPTION} sx={{ fontWeight: 600 }}>
                            {createNewSiteLabel}
                        </MuiMaterials.MenuItem>
                    ) : null}
                    {siteOptions.map((s) => (
                        <MuiMaterials.MenuItem key={s.siteGuid} value={s.siteGuid}>
                            {s.siteName}
                        </MuiMaterials.MenuItem>
                    ))}
                </MuiMaterials.TextField>
                <div style={confirmDialogActionsSx()}>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={confirmDialogButtonSx()}
                        onClick={handleCancel}
                        disabled={disableCancel}
                    >
                        {cancelLabel}
                    </MuiMaterials.Button>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={confirmDialogButtonSx()}
                        disabled={!pickerValue || loading}
                        onClick={() => onContinue?.(pickerValue)}
                    >
                        {continueLabel}
                    </MuiMaterials.Button>
                </div>
            </MuiMaterials.Paper>
        </MuiMaterials.Box>
    );
}

/**
 * Two-phase state machine that pairs with <SiteSelectPhase> and mirrors Site Setup's flow:
 *
 *   phase === "select" — show the picker, no site is loaded yet
 *   phase === "edit"   — a site is chosen; parent renders its normal body
 *
 * Returned state and callbacks are intended to be spread into WindowChrome + SiteSelectPhase:
 *
 *   const site = useSitePickerFlow();
 *   return (
 *     <WindowChrome
 *       title="Cable Health"
 *       subtitle={site.phase === "edit" ? site.selectedSiteName : null}
 *       actions={site.phase === "edit" ? <ChangeSiteButton onClick={site.reset} /> : null}
 *     >
 *       {site.phase === "select"
 *         ? <SiteSelectPhase {...site.selectProps} />
 *         : <YourBody siteGuid={site.selectedSiteGuid} />}
 *     </WindowChrome>
 *   );
 *
 * The hook seeds itself from `global.gw.selectedSite` on mount so navigating in from a Site Card
 * (which pre-selects) skips the picker entirely. Passing `initialSiteGuid` overrides that.
 */
export function useSitePickerFlow({
    initialSiteGuid = null,
    filterSite = null,
    onSiteSelected = null,
    allowCreateNewSite = false,
} = {}) {
    const seededGuid =
        initialSiteGuid
        ?? (typeof window !== "undefined" ? global.gw?.selectedSite?.siteGuid ?? null : null);

    const [pickerValue, setPickerValue] = useState("");
    const [selectedSiteGuid, setSelectedSiteGuid] = useState(seededGuid || "");
    const [sitesLoading, setSitesLoading] = useState(false);

    const selectedSiteName = useMemo(() => {
        const guid = selectedSiteGuid;
        if (!guid) return "";
        const sites = global.gw?.siteInfos ?? [];
        const match = sites.find(
            (s) => global.normalizeGuidForCompare(s?.siteGuid) === global.normalizeGuidForCompare(guid),
        );
        return match?.siteName ?? global.gw?.selectedSite?.siteName ?? "";
    }, [selectedSiteGuid]);

    const phase = selectedSiteGuid ? "edit" : "select";

    const handleContinue = useCallback(
        (guid) => {
            if (!guid) return;
            setSelectedSiteGuid(guid);
            if (typeof window !== "undefined" && global.gw?.selectedSite) {
                global.gw.selectedSite.siteGuid = guid;
                const sites = global.gw?.siteInfos ?? [];
                const match = sites.find(
                    (s) =>
                        global.normalizeGuidForCompare(s?.siteGuid) ===
                        global.normalizeGuidForCompare(guid),
                );
                if (match) {
                    global.gw.selectedSite.siteName = match.siteName;
                }
            }
            onSiteSelected?.(guid);
        },
        [onSiteSelected],
    );

    const reset = useCallback(() => {
        setSelectedSiteGuid("");
        setPickerValue("");
    }, []);

    return {
        phase,
        selectedSiteGuid,
        selectedSiteName,
        reset,
        setSitesLoading,
        selectProps: {
            value: pickerValue,
            onChange: setPickerValue,
            onContinue: handleContinue,
            loading: sitesLoading,
            filterSite,
            allowCreateNewSite,
        },
    };
}
